import { Component, DoCheck, inject, Input } from '@angular/core';
import { AufnahmePunktDocument, HorizontZeile, HumusAuflage, SubstratZeile, neueZeilenKennung, bodenformString, wasserformString, hatHumusForm, hatVegetationsformengruppe, humusformLabel, isB, isG, isM, isMudde, isQuellsediment, isTorf, moorMaechtigkeit, roundToNumber, roundToString, stfgString } from '../../../data-model';
import { feinbodenformText, hauptbodenformText } from '../bodenform-anzeige';
import { ensureSubstratRow } from '../hl-area/grube-profil-helpers';
import { substratZeileHatInhalt, horizontZeileHatInhalt, substratfolgeHatInhalt, horizontfolgeHatInhalt } from '../hl-area/profil-inhalt';
import { AUFLAGE_ZEILEN, AUFLAGE_PLATZHALTER, hatAuflage, auflageTiefeBis } from '../hl-area/humus-auflage';
import { FilzLage, OHNE_FILZ, angezeigteTiefe, auflagePlatzhalterZeile, filzLage } from '../../filz-tiefen';
import { AuthService } from 'src/app/auth.service';
import { DataService } from 'src/app/data.service';
import { EvaluationService } from '../../evaluation.service';
import { ValidationService } from '../../validation.service';
import { schichtNWSKEval } from '../../evaluators/nwsk';
import { applyEntwaesserungsVorschlag } from '../../evaluators/entwaesserung';
import { WechselLage, wechselLagenFuerAnzeige } from '../wechsel-gruppen';

/**
 * Eine Zeile der Uebersicht. Substrat- und Horizontfolge stehen nebeneinander
 * und werden nach der Tiefe ausgerichtet; deshalb kann eine Zeile nur die eine
 * oder nur die andere Seite fuehren.
 */
interface MergedRow {
	substratIndex: number | null;
	horizontIndex: number | null;
	tiefe: number;
	substratStripe: boolean;
	horizontStripe: boolean;
	/** Wo steht die Zeile in ihrer Wechsel-Kette? (Klammer neben „im Wechsel") */
	wechselLage: WechselLage;
}

@Component({
    standalone: false,
    selector: 'bp-bohrpunkt-boden',
    templateUrl: './bohrpunkt-boden.component.html',
    styleUrls: ['../hl-table.component.css', './bohrpunkt-boden.component.css'],
    
})
export class BohrpunktBodenComponent implements DoCheck {

  @Input() bp!: AufnahmePunktDocument;
  @Input() printView!: boolean;

	roundToNumber = roundToNumber;
	roundToString = roundToString;
	isTorf = isTorf;
	isMudde = isMudde;
	isQuellsediment = isQuellsediment;
	humusformLabel = humusformLabel;
	hatHumusForm = hatHumusForm;
	hatVegetationsformengruppe = hatVegetationsformengruppe;

	// Humusauflage: drei feste Zeilen oben in der Horizontfolge (siehe humus-auflage.ts).
	// Ohne Ueberschrift „Humusauflage" — die traegt nur die Eingabemaske (15.09.2026).
	auflageZeilen = AUFLAGE_ZEILEN;
	auflagePlatzhalter = AUFLAGE_PLATZHALTER;
	hatAuflage = hatAuflage;
	wasserformString = wasserformString;
	moorMaechtigkeit = moorMaechtigkeit;

	/**
	 * Filz: Die Auflage gehoert zum Profil, 0 cm liegt an der Oberkante des Ol
	 * (filz-tiefen.ts) - weil die Bodenform eine Filz-Bodenform ist ODER die
	 * eingegebene Auflage mehr als 11 cm misst. Die Uebersicht zieht dann die
	 * Gelaendeoberflaechen-Linie UEBER die Auflage, zeigt zu Ol/Of/Oh eine
	 * „Tiefe bis" und rueckt jede Tiefe darunter um die Auflage nach unten
	 * (`tiefe`). Wird in ngDoCheck einmal je Pruefung bestimmt; die Vorlage
	 * liest nur die Lage.
	 */
	lage: FilzLage = OHNE_FILZ;

	get filz(): boolean {
		return this.lage?.filz ?? false;
	}

  // Cache für merged rows
  mergedRows: MergedRow[] = [];
  private _lastSubstratTiefen = 'FORCE_INITIAL_COMPUTE';
  private _lastHorizontTiefen = 'FORCE_INITIAL_COMPUTE';
  private _lastWechsel = 'FORCE_INITIAL_COMPUTE';
  private _lastGefuellt = 'FORCE_INITIAL_COMPUTE';

  aus = inject(AuthService);
  private ds = inject(DataService);
  private es = inject(EvaluationService);
  private vs = inject(ValidationService);

  ngDoCheck(): void {
    // Defensive Initialisierung fehlender Objekte (z.B. bei älteren oder importierten Punkten)
    if (!this.bp.extendedHeader.ph) {
      this.bp.extendedHeader.ph = { wert1: undefined, ort1: '', wert2: undefined, ort2: '', durchschnitt: undefined };
    }
    if (!this.bp.extendedHeader.humusAuflage) {
      // Leer, nicht 0: Die Auflage steht jetzt als Zeilengruppe im Tiefenprofil,
      // und drei Nullen darin läsen sich wie "0 cm gemessen".
      this.bp.extendedHeader.humusAuflage = {};
    }

    // Grube: Lazy-Init der Sub-Objekte fuer Two-Way-Bindings (gefuege/feindurchwurzelung/probeentnahme).
    if (isG(this.bp) && Array.isArray(this.bp.Substratfolge)) {
      for (const row of this.bp.Substratfolge) ensureSubstratRow(row);
    }

    this.es.setContext(this.bp);
    this.vs.setContext(this.bp);
    this.bp.extendedHeader.nWsk = this.es.evaluate("nWSK") as number;
    applyEntwaesserungsVorschlag(this.bp);
    this.updateMergedRowsIfNeeded();
    this.aktualisiereFilz();
  }

	/**
	 * Die Filz-Lage kommt aus filz-tiefen.ts; dort steht auch, woher die
	 * Feinbodenformen-Liste kommt (die Listen kommen aus der Datenbank und sind
	 * beim ersten Blick nicht immer schon da - der Nachschlag holt sie nach).
	 */
	aktualisiereFilz(): void {
		this.lage = filzLage(this.bp);
	}

	/** Eine Tiefe, wie sie hier steht: eingegeben ab Mineralboden, bei Filz um die Auflage tiefer. */
	tiefe(eingegeben: number | undefined): number | undefined {
		return angezeigteTiefe(eingegeben, this.lage?.versatz ?? 0);
	}

	/** Zelle der Substratseite: „organische Auflage · 12 cm", bei Filz mit dem Hinweis dazu. */
	auflageZelle(): string {
		return auflagePlatzhalterZeile(AUFLAGE_PLATZHALTER, this.bp);
	}

	/** „Tiefe bis" einer Auflagezeile - nur bei Filz gezeigt. */
	auflageTiefeBis(schluessel: keyof HumusAuflage): number | undefined {
		return auflageTiefeBis(this.bp?.extendedHeader?.humusAuflage, schluessel);
	}

  private updateMergedRowsIfNeeded(): void {
    const substratTiefen = this.bp.Substratfolge?.map(s => s.tiefe).join(',') ?? '';
    const horizontTiefen = this.bp.Horizontfolge?.map(h => h.tiefe).join(',') ?? '';
    // Die Wechsel-Haken gehoeren in den Vergleich: Wer hier einen setzt, aendert
    // keine Tiefe — die Klammer bliebe sonst stehen, wie sie war.
    const wechsel = this.bp.Substratfolge?.map(s => s.wechselschichtung ? 'x' : '.').join('') ?? '';
    // Leere Zeilen blendet computeMergedRows aus. Bei 0 cm entscheidet der Inhalt, nicht
    // die Tiefe: Wer per „Aus anderem Punkt übernehmen" dieselben Tiefen mit anderer
    // oberster Zeile bekommt, sah sonst die alte Auswahl (fehlende oder leere Zeile).
    const gefuellt = (this.bp.Substratfolge?.map(s => substratZeileHatInhalt(s) ? 'x' : '.').join('') ?? '')
      + '/' + (this.bp.Horizontfolge?.map(h => horizontZeileHatInhalt(h) ? 'x' : '.').join('') ?? '');

    if (substratTiefen !== this._lastSubstratTiefen || horizontTiefen !== this._lastHorizontTiefen
        || wechsel !== this._lastWechsel || gefuellt !== this._lastGefuellt) {
      this.mergedRows = this.computeMergedRows();
      this._lastSubstratTiefen = substratTiefen;
      this._lastHorizontTiefen = horizontTiefen;
      this._lastWechsel = wechsel;
      this._lastGefuellt = gefuellt;
    }
  }

  stfg() {
		return stfgString(this.bp);
  }

  onAddSubstratZeile() {
    this.bp.Substratfolge.push({ id: neueZeilenKennung() } as SubstratZeile);
  }

  onRemoveSubstratZeile(i: number) {
    this.bp.Substratfolge.splice(i, 1);
  }

  onAddHorizontZeile() {
    this.bp.Horizontfolge.push({ id: neueZeilenKennung() } as HorizontZeile);
  }

  onRemoveHorizontZeile(i: number) {
    this.bp.Horizontfolge.splice(i, 1);
  }

  isB() {
    return isB(this.bp);
  }

  isG() {
    return isG(this.bp);
  }

  isM() {
    return isM(this.bp);
  }

	bodenformString(which: string) {
		return bodenformString(this.bp.header.bodenform, which);
	}

	qualityString(quality: number) {
		const e = this.ds.getHardcoded('Qualitätsstufen').find((entry: any) => entry.id === quality);
		return e ? e.name : "unbekannt";
	}
	
	/**
	 * Steht in der Substratfolge nichts, wird der Platz benannt statt leer
	 * gelassen — dasselbe fuer die Horizontfolge. Die Regel steht in
	 * profil-inhalt.ts und entscheidet auch, welche Zeilen ueberhaupt erscheinen.
	 */
	substratfolgeLeer(): boolean {
		return !substratfolgeHatInhalt(this.bp?.Substratfolge);
	}

	horizontfolgeLeer(): boolean {
		return !horizontfolgeHatInhalt(this.bp?.Horizontfolge);
	}

	colSpanSubstrate(): number {
		// B: 9, M: 15 (3 Torfarten), G: 22 (13 Grube-Zusatzspalten — alle Werte je eine Spalte)
		if (isM(this.bp)) return 15;
		if (isG(this.bp)) return 22;
		return 9;
	}

  // Die Zusammensetzung steht in bodenform-anzeige.ts - der Kopf des
  // Profilbildes zeigt dieselben Angaben und soll nicht seine eigene Fassung
  // davon fuehren.
  hauptbodenform = () => hauptbodenformText(this.bp);
  feinbodenform = () => feinbodenformText(this.bp);

  ph1Label(): string {
    const ort = this.bp.extendedHeader.ph?.ort1;
    return ort ? `pH₁ (${ort})` : 'pH₁';
  }

  karbonattiefe(): number | undefined {
    return this.es.evaluate("Karbonattiefe") as number | undefined;
  }

  vWert(): string | undefined {
    const labor = this.bp.extendedHeader.vWertSaugsaum;
    const vermutet = this.bp.extendedHeader.vWertSaugsaumVermutet;
    if (labor !== undefined && labor !== null) {
      return String(labor);
    }
    return vermutet;
  }

  vWertLabel(): string {
    const labor = this.bp.extendedHeader.vWertSaugsaum;
    const vermutet = this.bp.extendedHeader.vWertSaugsaumVermutet;
    if (labor !== undefined && labor !== null) {
      return 'V-Wert (Labor)';
    }
    if (vermutet) {
      return 'V-Wert (geschätzt)';
    }
    return 'V-Wert';
  }

  schichtNWSK(i: number): number | undefined {
    // Direkt den Evaluator aufrufen, um Kontextwechsel-Probleme bei Multi-Print zu vermeiden
    const result = schichtNWSKEval(this.bp, i) as number | undefined;
    return result !== undefined ? Math.round(result) : undefined;
  }

  // Merged rows für synchronisierte Darstellung von Substrat- und Horizontfolge
  private computeMergedRows(): MergedRow[] {
    const substratTiefen = this.bp.Substratfolge.map((s, i) => ({ tiefe: s.tiefe, type: 'substrat' as const, index: i }));
    const horizontTiefen = this.bp.Horizontfolge.map((h, i) => ({ tiefe: h.tiefe, type: 'horizont' as const, index: i }));

    // Alle einzigartigen Tiefen sammeln und sortieren
    const alleTiefen = new Set<number>();
    substratTiefen.forEach(s => { if (s.tiefe !== undefined) alleTiefen.add(s.tiefe); });
    horizontTiefen.forEach(h => { if (h.tiefe !== undefined) alleTiefen.add(h.tiefe); });

    const sortedTiefen = Array.from(alleTiefen).sort((a, b) => a - b);

    // Für jede Tiefe prüfen, ob Substrat und/oder Horizont existiert
    const result: MergedRow[] = [];

    // Tracke, welche Indizes bereits verwendet wurden
    const usedSubstratIndices = new Set<number>();
    const usedHorizontIndices = new Set<number>();

    // Erste Zeile: Immer Index 0 für beide (falls vorhanden)
    const firstSubstrat = substratTiefen.length > 0 ? substratTiefen[0] : null;
    const firstHorizont = horizontTiefen.length > 0 ? horizontTiefen[0] : null;

    if (firstSubstrat || firstHorizont) {
      if (firstSubstrat) usedSubstratIndices.add(0);
      if (firstHorizont) usedHorizontIndices.add(0);

      // Tiefe für die erste Zeile: das Minimum der beiden ersten Tiefen (falls definiert)
      const firstTiefe = Math.min(
        firstSubstrat?.tiefe ?? Infinity,
        firstHorizont?.tiefe ?? Infinity
      );

      result.push({
        substratIndex: firstSubstrat ? 0 : null,
        horizontIndex: firstHorizont ? 0 : null,
        tiefe: firstTiefe === Infinity ? 0 : firstTiefe,
        substratStripe: false, // wird später berechnet
        horizontStripe: false, // wird später berechnet
        wechselLage: 'keine'   // wird später berechnet
      });
    }

    // Restliche Zeilen nach Tiefe sortiert
    for (const tiefe of sortedTiefen) {
      const substrat = substratTiefen.find(s => s.tiefe === tiefe && !usedSubstratIndices.has(s.index));
      const horizont = horizontTiefen.find(h => h.tiefe === tiefe && !usedHorizontIndices.has(h.index));

      // Überspringe, wenn beide null sind (wurde bereits in erster Zeile behandelt)
      if (!substrat && !horizont) continue;

      if (substrat) usedSubstratIndices.add(substrat.index);
      if (horizont) usedHorizontIndices.add(horizont.index);

      result.push({
        substratIndex: substrat ? substrat.index : null,
        horizontIndex: horizont ? horizont.index : null,
        tiefe,
        substratStripe: false, // wird später berechnet
        horizontStripe: false, // wird später berechnet
        wechselLage: 'keine'   // wird später berechnet
      });
    }

    // Leere Zeilen raus: Eine angelegte, noch nicht gefuellte Zeile ist in der
    // Leseansicht nur ein grauer Streifen. Dieselbe Regel wie beim Platzhalter
    // (profil-inhalt.ts) — sonst stuende beides nebeneinander.
    const gefuellt = result.filter(r =>
      substratZeileHatInhalt(this.bp.Substratfolge?.[r.substratIndex ?? -1])
      || horizontZeileHatInhalt(this.bp.Horizontfolge?.[r.horizontIndex ?? -1])
    );
    result.length = 0;
    result.push(...gefuellt);

    // Stripes berechnen: Von unten nach oben
    // Echte Zeilen bekommen abwechselnd Farben, Leerzeilen bekommen die Farbe der echten Zeile DARUNTER
    let currentSubstratStripe = false;
    let currentHorizontStripe = false;

    for (let i = result.length - 1; i >= 0; i--) {
      const row = result[i];

      // Substrat: Erst wechseln bei echter Zeile, dann Farbe zuweisen
      if (row.substratIndex !== null) {
        currentSubstratStripe = !currentSubstratStripe;
      }
      row.substratStripe = currentSubstratStripe;

      // Horizont: gleiche Logik
      if (row.horizontIndex !== null) {
        currentHorizontStripe = !currentHorizontStripe;
      }
      row.horizontStripe = currentHorizontStripe;
    }

    // Wechsel-Klammer: Der Haken an einer Substratzeile heisst „wechselt mit
    // der Zeile darunter" (Regel in wechsel-gruppen.ts). Hier steht zwischen
    // zwei Substratzeilen mitunter eine Zeile, die nur einen Horizont fuehrt —
    // durch die muss die Linie durchlaufen, sonst risse die Klammer mitten in
    // einer Kette auf.
    const lagen = wechselLagenFuerAnzeige(this.bp.Substratfolge, result.map(r => r.substratIndex));
    result.forEach((row, i) => row.wechselLage = lagen[i]);

    return result;
  }

  getSubstratRow(index: number | null): SubstratZeile | null {
    return index !== null ? this.bp.Substratfolge[index] : null;
  }

  getHorizontRow(index: number | null): HorizontZeile | null {
    return index !== null ? this.bp.Horizontfolge[index] : null;
  }
}
